import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
	GetSingleTripTicketData,
	GetTicketsTabData,
	TicketsTabTrip,
} from 'Services/Api/_HumanWritten/BookingWizardDataService';

interface UseTripQueryParams {
	fromLocationId: string;
	toLocationId: string;
	centerDate: Date;
	isDeparture: boolean;
	onlyAlteringReturn: boolean;
	bookingToEdit: string | null;
	userId: string;
	isStaff: boolean;
	enabled?: boolean;
}

const DATE_WINDOW_DAYS = 2;

/**
 * Fetches trips for a date range (center date ±2 days) in a single request.
 * Previously this fired one request per day (5 sequential calls); the endpoint now takes a
 * date range, so the whole window comes back in one round-trip.
 */
async function fetchTripsForDateRange(params: UseTripQueryParams): Promise<TicketsTabTrip[]> {
	const {
		centerDate,
		isDeparture,
		onlyAlteringReturn,
		fromLocationId,
		toLocationId,
		bookingToEdit,
		userId,
		isStaff,
	} = params;

	const dateFrom = dayjs(centerDate).add(-DATE_WINDOW_DAYS, 'day').startOf('day').toDate();
	const dateTo = dayjs(centerDate).add(DATE_WINDOW_DAYS, 'day').startOf('day').toDate();

	const data = await GetTicketsTabData(
		new AbortController().signal, // React Query handles cancellation
		fromLocationId,
		toLocationId,
		dateFrom,
		dateTo,
		isDeparture,
		onlyAlteringReturn,
		bookingToEdit,
		isStaff ? userId : undefined,
	);

	const ticketKey = isDeparture ? 'departureTickets' : 'returningTickets';
	const trips = data[ticketKey] || [];

	// Deduplicate by trip ID (a trip can surface under more than one start/end combination).
	const seenIds = new Set<string>();
	const allTrips: TicketsTabTrip[] = [];
	trips.forEach(trip => {
		if (!seenIds.has(trip.id)) {
			seenIds.add(trip.id);
			allTrips.push(trip);
		}
	});

	return allTrips;
}

interface FetchSingleTripParams {
	tripId: string;
	fromLocationId: string;
	toLocationId: string;
	isDeparture: boolean;
	onlyAlteringReturn: boolean;
	bookingToEdit: string | null;
	userId: string;
	isStaff: boolean;
}

async function fetchSingleTrip(params: FetchSingleTripParams): Promise<TicketsTabTrip[]> {
	const data = await GetSingleTripTicketData(
		new AbortController().signal,
		params.tripId,
		params.fromLocationId,
		params.toLocationId,
		params.isDeparture,
		params.onlyAlteringReturn,
		params.bookingToEdit,
		params.isStaff ? params.userId : undefined,
	);
	const ticketKey = params.isDeparture ? 'departureTickets' : 'returningTickets';
	return data[ticketKey] || [];
}

/**
 * Result surfaced to the tickets tab: matches React Query's `useQuery` return shape closely
 * but adds `isWindowLoading` for the alteration-mode background-prefetch path. While the
 * multi-day window query is still in flight, `data` is the single-trip placeholder (the
 * user's existing booking), `isLoading`/`isFetching` are false so the trip card renders, and
 * `isWindowLoading` is true so the UI can optionally surface a "loading more trips" hint.
 */
interface TripsQueryResult {
	data: TicketsTabTrip[] | undefined;
	isLoading: boolean;
	isFetching: boolean;
	isError: boolean;
	isWindowLoading: boolean;
	/**
	 * Fetch this leg's sailings again — after something on a card changed what the server would
	 * say about them, such as the booker joining a sailing's waitlist.
	 */
	refetch: () => void;
}

interface DepartureTripsQueryParams extends Omit<UseTripQueryParams, 'isDeparture' | 'centerDate'> {
	startDate: Date;
	ticketSelectionStartDate: Date;
	/**
	 * Trip id of the booking's existing departure leg, set in alteration mode so the user's
	 * existing trip card paints immediately while the multi-day window fetch runs in the
	 * background. Falsy outside alteration mode — only the window query fires.
	 */
	initialTripId?: string | null;
}

/**
 * Combines the window query with React Query's loading semantics so that the consumer can
 * keep its old `isLoading || isFetching` pattern: while single-trip data is in hand the
 * surface is "loaded"; the window swap-in is silent (visible only via `isWindowLoading`).
 */
function mergeTripsQueries(
	windowQuery: ReturnType<typeof useQuery<TicketsTabTrip[]>>,
	singleTripQuery: ReturnType<typeof useQuery<TicketsTabTrip[]>>,
	singleEnabled: boolean,
): TripsQueryResult {
	const data = windowQuery.data ?? (singleEnabled ? singleTripQuery.data : undefined);
	const hasAnyData = data != null;

	return {
		data,
		isLoading: !hasAnyData && (windowQuery.isLoading || (singleEnabled && singleTripQuery.isLoading)),
		isFetching: !hasAnyData && windowQuery.isFetching,
		isError: windowQuery.isError || (singleEnabled && singleTripQuery.isError),
		isWindowLoading: !windowQuery.isFetched && (windowQuery.isLoading || windowQuery.isFetching),
		refetch: () => {
			windowQuery.refetch();
			if (singleEnabled) {
				singleTripQuery.refetch();
			}
		},
	};
}

/**
 * React Query hook for fetching departure trips
 */
export function useDepartureTripsQuery(params: DepartureTripsQueryParams): TripsQueryResult {
	const windowQuery = useQuery({
		queryKey: [
			'trips',
			'departure',
			params.fromLocationId,
			params.toLocationId,
			// Use startDate in queryKey so it only refetches when filter date changes
			dayjs(params.startDate).format('YYYY-MM-DD'),
			params.onlyAlteringReturn,
			params.bookingToEdit,
			params.userId,
		],
		queryFn: () =>
			fetchTripsForDateRange({
				fromLocationId: params.fromLocationId,
				toLocationId: params.toLocationId,
				centerDate: params.startDate,
				isDeparture: true,
				onlyAlteringReturn: params.onlyAlteringReturn,
				bookingToEdit: params.bookingToEdit,
				userId: params.userId,
				isStaff: params.isStaff,
			}),
		enabled: params.enabled !== false,
		staleTime: 60000, // 1 minute
	});

	const singleEnabled = params.enabled !== false && !!params.initialTripId && !!params.bookingToEdit;
	const singleTripQuery = useQuery({
		queryKey: [
			'trips',
			'departure-single',
			params.fromLocationId,
			params.toLocationId,
			params.initialTripId,
			params.onlyAlteringReturn,
			params.bookingToEdit,
			params.userId,
		],
		queryFn: () =>
			fetchSingleTrip({
				tripId: params.initialTripId!,
				fromLocationId: params.fromLocationId,
				toLocationId: params.toLocationId,
				isDeparture: true,
				onlyAlteringReturn: params.onlyAlteringReturn,
				bookingToEdit: params.bookingToEdit,
				userId: params.userId,
				isStaff: params.isStaff,
			}),
		enabled: singleEnabled,
		staleTime: 60000,
	});

	return mergeTripsQueries(windowQuery, singleTripQuery, singleEnabled);
}

interface ReturnTripsQueryParams extends Omit<UseTripQueryParams, 'isDeparture' | 'centerDate'> {
	endDate: Date;
	ticketSelectionEndDate: Date;
	/** Trip id of the booking's existing return leg, when altering an existing return. */
	initialTripId?: string | null;
}

/**
 * React Query hook for fetching return trips
 */
export function useReturnTripsQuery(params: ReturnTripsQueryParams): TripsQueryResult {
	const windowQuery = useQuery({
		queryKey: [
			'trips',
			'return',
			params.fromLocationId,
			params.toLocationId,
			// Use endDate in queryKey so it only refetches when filter date changes
			dayjs(params.endDate).format('YYYY-MM-DD'),
			params.onlyAlteringReturn,
			params.bookingToEdit,
			params.userId,
		],
		queryFn: () =>
			fetchTripsForDateRange({
				fromLocationId: params.fromLocationId,
				toLocationId: params.toLocationId,
				centerDate: params.endDate,
				isDeparture: params.onlyAlteringReturn,
				onlyAlteringReturn: params.onlyAlteringReturn,
				bookingToEdit: params.bookingToEdit,
				userId: params.userId,
				isStaff: params.isStaff,
			}),
		enabled: params.enabled !== false,
		staleTime: 60000, // 1 minute
	});

	const singleEnabled = params.enabled !== false && !!params.initialTripId && !!params.bookingToEdit;
	const singleTripQuery = useQuery({
		queryKey: [
			'trips',
			'return-single',
			params.fromLocationId,
			params.toLocationId,
			params.initialTripId,
			params.onlyAlteringReturn,
			params.bookingToEdit,
			params.userId,
		],
		queryFn: () =>
			fetchSingleTrip({
				tripId: params.initialTripId!,
				fromLocationId: params.fromLocationId,
				toLocationId: params.toLocationId,
				isDeparture: params.onlyAlteringReturn,
				onlyAlteringReturn: params.onlyAlteringReturn,
				bookingToEdit: params.bookingToEdit,
				userId: params.userId,
				isStaff: params.isStaff,
			}),
		enabled: singleEnabled,
		staleTime: 60000,
	});

	return mergeTripsQueries(windowQuery, singleTripQuery, singleEnabled);
}
